// src/components/CollectionBookExport.jsx
import React, { useState } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";

/**
 * Objectif:
 * - PDF toujours généré (même si images KO, webp, 403, etc.)
 * - 2 pages par œuvre, total variable
 * - placeholders propres si contenu manquant
 */

const CollectionBookExport = ({ selectedArtworks, collectionData, userData }) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [bookConfig, setBookConfig] = useState({
    collectionTitle: "",
    subtitle: "",
    introductionText: "",
    collectorPortrait: "", // url optionnelle
    curatorText: "",
    organization: "chronological", // chronological, artist, thematic, period
    includeEssay: false,
    essayText: "",
    essayAuthor: "",
    includeIndex: true,
    includeColophon: true,
  });

  const safeFileName = (s) =>
    String(s || "")
      .replace(/[/\\?%*:|"<>]/g, "-")
      .replace(/\s+/g, " ")
      .trim();

  // ---------- PDF Helpers ----------
  const createPdfWithHelpers = () => {
    const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
    const margin = 20;

    const pageHeight = doc.internal.pageSize.getHeight
      ? doc.internal.pageSize.getHeight()
      : doc.internal.pageSize.height;

    const pageWidth = doc.internal.pageSize.getWidth
      ? doc.internal.pageSize.getWidth()
      : doc.internal.pageSize.width;

    let y = 20;

    const newPage = (startY = 30) => {
      doc.addPage("a4", "p");
      doc.setPage(doc.getNumberOfPages());
      y = startY;
    };

    const ensureSpace = (needed = 10) => {
      if (y + needed > pageHeight - margin) newPage(30);
    };

    const addWrappedText = (text, x, maxWidth, lineHeight = 5) => {
      const lines = doc.splitTextToSize(String(text || ""), maxWidth);
      lines.forEach((ln) => {
        ensureSpace(lineHeight + 2);
        doc.text(ln, x, y);
        y += lineHeight;
      });
      return lines.length;
    };

    const placeholderBox = (x, top, w, h, label = "Placeholder") => {
      doc.setDrawColor(200);
      doc.rect(x, top, w, h);
      doc.setTextColor(140);
      doc.setFontSize(10);
      doc.text(label, x + w / 2, top + h / 2, { align: "center" });
      doc.setTextColor(0);
      doc.setDrawColor(0);
    };

    return {
      doc,
      margin,
      pageHeight,
      pageWidth,
      getY: () => y,
      setY: (v) => {
        y = v;
      },
      newPage,
      ensureSpace,
      addWrappedText,
      placeholderBox,
    };
  };

  // ---------- Image pipeline (ultra robuste) ----------
  const blobToDataURL = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const loadImageElement = (dataUrl) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl;
    });

  /**
   * Convertit n'importe quel blob image (png/webp/jpg) vers JPEG dataURL
   * + downscale si trop grand (évite crash mémoire jsPDF)
   */
  const convertToJpegDataUrl = async (blob, maxSide = 2000, quality = 0.82) => {
    const dataUrl = await blobToDataURL(blob);

    // si déjà jpeg et pas gigantesque, on peut garder
    if (typeof dataUrl === "string" && dataUrl.startsWith("data:image/jpeg")) {
      return { dataUrl, format: "JPEG" };
    }

    // sinon canvas => jpeg
    const img = await loadImageElement(dataUrl);
    const { width, height } = img;

    let targetW = width;
    let targetH = height;

    const maxDim = Math.max(width, height);
    if (maxDim > maxSide) {
      const ratio = maxSide / maxDim;
      targetW = Math.round(width * ratio);
      targetH = Math.round(height * ratio);
    }

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");

    // fond blanc (utile pour png transparents)
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, targetW, targetH);
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const jpegDataUrl = canvas.toDataURL("image/jpeg", quality);
    return { dataUrl: jpegDataUrl, format: "JPEG" };
  };

  /**
   * Fetch image en mode "no drama":
   * - si fetch échoue, retourne null (pas throw)
   * - si réponse n'est pas une image (403 html), retourne null
   */
  const fetchImageAsJpegDataUrlSafe = async (url) => {
    if (!url || typeof url !== "string") return null;

    try {
      // IMPORTANT: certains CDN/Supabase renvoient webp selon le navigateur
      // On force accept image/*, et on évite credentials.
      const res = await fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          Accept: "image/avif,image/webp,image/apng,image/*,*/*;q=0.8",
        },
      });

      if (!res.ok) {
        // 403/404 etc.
        return null;
      }

      const blob = await res.blob();

      // Vérif soft: si c'est du HTML (ex: 403 renvoie une page), on stop
      const type = (blob.type || "").toLowerCase();
      if (!type.startsWith("image/")) {
        return null;
      }

      // Convert vers JPEG stable pour jsPDF
      const jpeg = await convertToJpegDataUrl(blob);
      return jpeg; // {dataUrl, format:"JPEG"}
    } catch (e) {
      return null;
    }
  };

  // ---------- Main ----------
  const generateCollectionBook = async () => {
    setLoading(true);

    try {
      const artworksSafe = Array.isArray(selectedArtworks) ? selectedArtworks : [];

      const {
        doc,
        margin,
        pageWidth,
        setY,
        getY,
        newPage,
        ensureSpace,
        addWrappedText,
        placeholderBox,
      } = createPdfWithHelpers();

      // PAGE 1 - Couverture
      doc.setPage(1);
      setY(20);

      doc.setFontSize(32);
      doc.setFont("helvetica", "bold");
      doc.text(bookConfig.collectionTitle || "COLLECTION", pageWidth / 2, 100, { align: "center" });

      if (bookConfig.subtitle) {
        doc.setFontSize(18);
        doc.setFont("helvetica", "normal");
        doc.text(bookConfig.subtitle, pageWidth / 2, 115, { align: "center" });
      }

      doc.setFontSize(14);
      doc.setFont("helvetica", "italic");
      doc.text(userData?.name || collectionData?.name || "", pageWidth / 2, 140, { align: "center" });

      const coverBox = { x: 60, y: 160, w: 90, h: 90 };
      const coverImgUrl = artworksSafe[0]?.image_url;

      const coverImg = await fetchImageAsJpegDataUrlSafe(coverImgUrl);
      if (coverImg?.dataUrl) {
        try {
          doc.addImage(coverImg.dataUrl, coverImg.format || "JPEG", coverBox.x, coverBox.y, coverBox.w, coverBox.h);
        } catch (e) {
          placeholderBox(coverBox.x, coverBox.y, coverBox.w, coverBox.h, "Image couverture");
        }
      } else {
        placeholderBox(coverBox.x, coverBox.y, coverBox.w, coverBox.h, "Image couverture");
      }

      // PAGE 2 - Intention
      newPage(120);
      doc.setFontSize(14);
      doc.setFont("helvetica", "italic");

      const intentionQuote =
        bookConfig.introductionText ||
        `"L'art est le plus beau des mensonges qui nous permette de reconnaître la vérité."`;

      const wrappedQuote = doc.splitTextToSize(intentionQuote, 140);
      wrappedQuote.forEach((line) => {
        doc.text(line, pageWidth / 2, getY(), { align: "center" });
        setY(getY() + 7);
      });

      // PAGE 3 - Portrait collectionneur
      newPage(50);

      const portraitBox = { x: 70, y: getY(), w: 70, h: 90 };
      if (bookConfig.collectorPortrait) {
        const portraitImg = await fetchImageAsJpegDataUrlSafe(bookConfig.collectorPortrait);
        if (portraitImg?.dataUrl) {
          try {
            doc.addImage(portraitImg.dataUrl, portraitImg.format || "JPEG", portraitBox.x, portraitBox.y, portraitBox.w, portraitBox.h);
          } catch (e) {
            placeholderBox(portraitBox.x, portraitBox.y, portraitBox.w, portraitBox.h, "Portrait collectionneur");
          }
        } else {
          placeholderBox(portraitBox.x, portraitBox.y, portraitBox.w, portraitBox.h, "Portrait collectionneur");
        }
      } else {
        // squelette
        placeholderBox(portraitBox.x, portraitBox.y, portraitBox.w, portraitBox.h, "Portrait (optionnel)");
      }
      setY(portraitBox.y + 100);

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(userData?.name || "Le Collectionneur", pageWidth / 2, getY(), { align: "center" });
      setY(getY() + 10);

      doc.setFontSize(12);
      doc.setFont("helvetica", "italic");
      doc.text(`Collectionneur depuis ${userData?.collecting_since || "1995"}`, pageWidth / 2, getY(), { align: "center" });

      // PAGE 4 - Intro collection
      newPage(30);
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("La Collection", pageWidth / 2, getY(), { align: "center" });
      setY(getY() + 20);

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");

      const introText =
        bookConfig.curatorText ||
        collectionData?.history ||
        `Cette collection, constituée avec passion au fil des années, reflète un engagement profond envers l'art contemporain.

Chaque œuvre a été choisie pour sa singularité, sa qualité exceptionnelle et sa capacité à dialoguer avec les autres pièces de l'ensemble.

Au-delà de l'acquisition, il s'agit d'une démarche de préservation et de transmission d'un patrimoine culturel unique.`;

      addWrappedText(introText, margin, pageWidth - 2 * margin, 6);

      const themes = Array.isArray(collectionData?.main_themes) ? collectionData.main_themes : [];
      if (themes.length) {
        setY(getY() + 10);
        ensureSpace(40);

        doc.setFont("helvetica", "bold");
        doc.text("Axes de collection", margin, getY());
        setY(getY() + 8);

        doc.setFont("helvetica", "normal");
        themes.forEach((t) => {
          ensureSpace(8);
          doc.text(`• ${t}`, margin + 5, getY());
          setY(getY() + 6);
        });
      }

      // Organisation œuvres
      let organized = [...artworksSafe];
      switch (bookConfig.organization) {
        case "artist":
          organized.sort((a, b) => (a?.artist || "").localeCompare(b?.artist || ""));
          break;
        case "chronological":
          organized.sort((a, b) => parseInt(a?.year || "9999", 10) - parseInt(b?.year || "9999", 10));
          break;
        case "period":
          organized.sort((a, b) => {
            const dec = (year) => Math.floor(parseInt(year || "9999", 10) / 10) * 10;
            return dec(a?.year) - dec(b?.year);
          });
          break;
        case "thematic":
        default:
          break;
      }

      // Squelette si 0 œuvres sélectionnées (mais ton bouton est déjà disabled, on sécurise quand même)
      const perArtwork =
        organized.length > 0
          ? organized
          : [
              {
                artist: "Artiste",
                title: "Sans titre",
                year: "—",
                medium: "Technique non spécifiée",
                dimensions: "Dimensions non spécifiées",
                description: "",
                acquisition_source: "",
                acquisition_date: "",
                exhibitions: [],
                inventory_number: "",
                image_url: "",
              },
            ];

      // 2 pages par œuvre, TOUJOURS
      for (let i = 0; i < perArtwork.length; i++) {
        const artwork = perArtwork[i];

        // Page 1 œuvre: visuel pleine page
        newPage(30);
        const imgW = 170;
        const imgH = 220;
        const x = (pageWidth - imgW) / 2;

        const artworkImg = await fetchImageAsJpegDataUrlSafe(artwork.image_url);
        if (artworkImg?.dataUrl) {
          try {
            doc.addImage(artworkImg.dataUrl, artworkImg.format || "JPEG", x, getY(), imgW, imgH);
          } catch (e) {
            placeholderBox(x, getY(), imgW, imgH, `Image œuvre ${i + 1}`);
          }
        } else {
          placeholderBox(x, getY(), imgW, imgH, `Image œuvre ${i + 1}`);
        }

        // Page 2 œuvre: cartel + texte + provenance + expositions
        newPage(30);

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(artwork.artist || "Artiste inconnu", margin, getY());
        setY(getY() + 10);

        doc.setFontSize(12);
        doc.setFont("helvetica", "italic");
        doc.text(artwork.title || "Sans titre", margin, getY());
        doc.setFont("helvetica", "normal");
        doc.text(String(artwork.year || ""), pageWidth - margin, getY(), { align: "right" });
        setY(getY() + 8);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(artwork.medium || "Technique non spécifiée", margin, getY());
        setY(getY() + 5);
        doc.text(artwork.dimensions || "Dimensions non spécifiées", margin, getY());
        setY(getY() + 12);

        if (artwork.description) {
          addWrappedText(artwork.description, margin, pageWidth - 2 * margin, 5);
          setY(getY() + 6);
        } else {
          // squelette (optionnel mais “parlant”)
          doc.setFontSize(9);
          doc.setTextColor(120);
          doc.text("[Texte de présentation de l’œuvre]", margin, getY());
          doc.setTextColor(0);
          doc.setFontSize(10);
          setY(getY() + 8);
        }

        // Provenance (squelette toujours)
        ensureSpace(40);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("PROVENANCE", margin, getY());
        setY(getY() + 6);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        if (artwork.acquisition_source) {
          doc.text(`Acquis auprès de ${artwork.acquisition_source}`, margin, getY());
          setY(getY() + 5);
        } else {
          doc.text("Collection particulière", margin, getY());
          setY(getY() + 5);
        }

        if (artwork.acquisition_date) {
          const acqYear = new Date(artwork.acquisition_date).getFullYear();
          doc.text(`Collection actuelle depuis ${Number.isNaN(acqYear) ? "[Année]" : acqYear}`, margin, getY());
          setY(getY() + 5);
        } else {
          doc.setFontSize(9);
          doc.setTextColor(120);
          doc.text("Date d’entrée en collection : [à compléter]", margin, getY());
          doc.setTextColor(0);
          doc.setFontSize(10);
          setY(getY() + 5);
        }
        setY(getY() + 6);

        // Expositions (si présent) sinon squelette léger
        ensureSpace(28);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("EXPOSITIONS", margin, getY());
        setY(getY() + 6);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        if (Array.isArray(artwork.exhibitions) && artwork.exhibitions.length > 0) {
          artwork.exhibitions.slice(0, 3).forEach((expo) => {
            const expoText = `${expo.date || "[Date]"} : ${expo.name || "[Exposition]"}, ${expo.venue || "[Lieu]"}`;
            addWrappedText(expoText, margin, pageWidth - 2 * margin, 5);
          });
          setY(getY() + 2);
        } else {
          doc.setFontSize(9);
          doc.setTextColor(120);
          doc.text("[Expositions principales, si applicable]", margin, getY());
          doc.setTextColor(0);
          doc.setFontSize(10);
          setY(getY() + 8);
        }

        // Inventaire
        if (artwork.inventory_number) {
          ensureSpace(10);
          doc.setFont("helvetica", "italic");
          doc.setFontSize(8);
          doc.text(`N° inv. ${artwork.inventory_number}`, margin, getY());
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
        }
      }

      // Essai critique
      if (bookConfig.includeEssay && bookConfig.essayText) {
        newPage(30);

        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text("Essai critique", pageWidth / 2, getY(), { align: "center" });
        setY(getY() + 15);

        if (bookConfig.essayAuthor) {
          doc.setFontSize(12);
          doc.setFont("helvetica", "italic");
          doc.text(`par ${bookConfig.essayAuthor}`, pageWidth / 2, getY(), { align: "center" });
          setY(getY() + 18);
        }

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");

        String(bookConfig.essayText)
          .split("\n\n")
          .forEach((p) => {
            if (!p.trim()) return;
            addWrappedText(p, margin, pageWidth - 2 * margin, 5);
            setY(getY() + 6);
          });
      }

      // Index
      if (bookConfig.includeIndex) {
        newPage(30);

        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("Liste des œuvres", pageWidth / 2, getY(), { align: "center" });
        setY(getY() + 15);

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");

        perArtwork.forEach((a, idx) => {
          ensureSpace(24);

          const entry = [
            `${idx + 1}.`,
            a.artist || "Artiste inconnu",
            a.title || "Sans titre",
            a.year || "-",
            a.medium || "",
            a.dimensions || "",
          ]
            .filter(Boolean)
            .join(" • ");

          addWrappedText(entry, margin, pageWidth - 2 * margin, 4);

          if (a.acquisition_source) {
            doc.setFont("helvetica", "italic");
            doc.setFontSize(8);
            ensureSpace(8);
            doc.text(`Provenance : ${a.acquisition_source}`, margin + 5, getY());
            setY(getY() + 4);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
          }

          if (a.inventory_number) {
            doc.setFont("helvetica", "italic");
            doc.setFontSize(7);
            ensureSpace(8);
            doc.text(`N° ${a.inventory_number}`, margin + 5, getY());
            setY(getY() + 4);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
          }

          setY(getY() + 4);
        });
      }

      // Colophon
      if (bookConfig.includeColophon) {
        newPage(120);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");

        const colophon = [
          "Crédits photographiques",
          "[Nom photographe / Studio]",
          "",
          "Remerciements",
          "[Personnes à remercier]",
          "",
          "Mentions légales",
          `© ${new Date().getFullYear()} ${userData?.name || "Collection"}`,
          "Tous droits réservés",
          "",
          "Document généré par Artkiv",
          `Date d'édition : ${new Date().toLocaleDateString("fr-FR")}`,
        ];

        colophon.forEach((line) => {
          if (!line) {
            setY(getY() + 5);
          } else {
            doc.text(line, pageWidth / 2, getY(), { align: "center" });
            setY(getY() + 5);
          }
        });
      }

      const fileName = `Book_Collection_${safeFileName(bookConfig.collectionTitle || collectionData?.name || "Collection")}_${new Date()
        .toISOString()
        .split("T")[0]}.pdf`;

      doc.save(fileName);

      setShowModal(false);
      setLoading(false);
    } catch (error) {
      console.error("Erreur génération book:", error);
      alert("Erreur lors de la génération du book. Veuillez réessayer.");
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        disabled={!selectedArtworks || selectedArtworks.length === 0}
        style={{
          padding: "12px 24px",
          backgroundColor: selectedArtworks?.length > 0 ? "#000" : "#ccc",
          color: "#fff",
          border: "none",
          borderRadius: "2px",
          fontSize: "14px",
          fontFamily: "Georgia, serif",
          cursor: selectedArtworks?.length > 0 ? "pointer" : "not-allowed",
          transition: "all 0.2s ease",
        }}
      >
        Book collection prestige ({selectedArtworks?.length || 0} œuvre{selectedArtworks?.length > 1 ? "s" : ""})
      </button>

      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "40px",
              borderRadius: "2px",
              maxWidth: "700px",
              width: "90%",
              maxHeight: "85vh",
              overflowY: "auto",
              fontFamily: "Georgia, serif",
            }}
          >
            <h2 style={{ fontSize: "24px", fontWeight: "400", marginBottom: "30px", color: "#000" }}>
              Configuration Book Collection
            </h2>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#000", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Titre de la collection *
              </label>
              <input
                type="text"
                value={bookConfig.collectionTitle}
                onChange={(e) => setBookConfig({ ...bookConfig, collectionTitle: e.target.value })}
                placeholder="Ex: Collection d'Art Contemporain"
                style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif" }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#000", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Sous-titre (optionnel)
              </label>
              <input
                type="text"
                value={bookConfig.subtitle}
                onChange={(e) => setBookConfig({ ...bookConfig, subtitle: e.target.value })}
                placeholder="Ex: Abstraction française 1950-2020"
                style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif" }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#000", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Citation ou texte d'intention
              </label>
              <textarea
                value={bookConfig.introductionText}
                onChange={(e) => setBookConfig({ ...bookConfig, introductionText: e.target.value })}
                placeholder="Citation inspirante ou texte court en page 2..."
                rows={3}
                style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif", resize: "vertical" }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#000", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Texte introductif sur la collection
              </label>
              <textarea
                value={bookConfig.curatorText}
                onChange={(e) => setBookConfig({ ...bookConfig, curatorText: e.target.value })}
                placeholder="Présentation détaillée de votre démarche de collectionneur, histoire, axes..."
                rows={6}
                style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif", resize: "vertical" }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#000", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Organisation des œuvres
              </label>
              <select
                value={bookConfig.organization}
                onChange={(e) => setBookConfig({ ...bookConfig, organization: e.target.value })}
                style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif", backgroundColor: "#fff" }}
              >
                <option value="chronological">Chronologique (par date)</option>
                <option value="artist">Par artiste (alphabétique)</option>
                <option value="thematic">Par thématique (ordre sélection)</option>
                <option value="period">Par période esthétique</option>
              </select>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "flex", alignItems: "center", fontSize: "14px", cursor: "pointer", marginBottom: "10px" }}>
                <input
                  type="checkbox"
                  checked={bookConfig.includeEssay}
                  onChange={(e) => setBookConfig({ ...bookConfig, includeEssay: e.target.checked })}
                  style={{ marginRight: "10px" }}
                />
                Inclure un essai critique (historien d'art)
              </label>

              {bookConfig.includeEssay && (
                <>
                  <input
                    type="text"
                    value={bookConfig.essayAuthor}
                    onChange={(e) => setBookConfig({ ...bookConfig, essayAuthor: e.target.value })}
                    placeholder="Nom de l'auteur de l'essai"
                    style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif", marginBottom: "10px" }}
                  />
                  <textarea
                    value={bookConfig.essayText}
                    onChange={(e) => setBookConfig({ ...bookConfig, essayText: e.target.value })}
                    placeholder="Texte critique sur la cohérence et l'importance de la collection..."
                    rows={6}
                    style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif", resize: "vertical" }}
                  />
                </>
              )}
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "flex", alignItems: "center", fontSize: "14px", cursor: "pointer", marginBottom: "8px" }}>
                <input
                  type="checkbox"
                  checked={bookConfig.includeIndex}
                  onChange={(e) => setBookConfig({ ...bookConfig, includeIndex: e.target.checked })}
                  style={{ marginRight: "10px" }}
                />
                Inclure un index complet des œuvres
              </label>

              <label style={{ display: "flex", alignItems: "center", fontSize: "14px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={bookConfig.includeColophon}
                  onChange={(e) => setBookConfig({ ...bookConfig, includeColophon: e.target.checked })}
                  style={{ marginRight: "10px" }}
                />
                Inclure un colophon (crédits, mentions légales)
              </label>
            </div>

            <div style={{ padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "2px", marginBottom: "20px", fontSize: "13px", color: "#666" }}>
              <strong>Estimation pages :</strong>{" "}
              {5 + (selectedArtworks?.length * 2 || 0) + (bookConfig.includeEssay ? 5 : 0) + (bookConfig.includeIndex ? 3 : 0) + (bookConfig.includeColophon ? 1 : 0)}{" "}
              pages environ
            </div>

            <div style={{ display: "flex", gap: "15px", marginTop: "30px", paddingTop: "20px", borderTop: "1px solid #eee" }}>
              <button
                onClick={() => setShowModal(false)}
                disabled={loading}
                style={{ flex: 1, padding: "12px", backgroundColor: "#f5f5f5", color: "#000", border: "1px solid #ddd", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif", cursor: loading ? "not-allowed" : "pointer" }}
              >
                Annuler
              </button>
              <button
                onClick={generateCollectionBook}
                disabled={loading || !bookConfig.collectionTitle}
                style={{ flex: 1, padding: "12px", backgroundColor: loading || !bookConfig.collectionTitle ? "#999" : "#000", color: "#fff", border: "none", borderRadius: "2px", fontSize: "14px", fontFamily: "Georgia, serif", cursor: loading || !bookConfig.collectionTitle ? "not-allowed" : "pointer" }}
              >
                {loading ? "Génération..." : "Générer le book PDF"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CollectionBookExport;
